import React, { useState } from "react";
import { format } from "date-fns";
import "./CsvImport.css";
import ExcelImporter from "../../components/ExcelImporter";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { timestamp } from "../../firebase/config";

const parseImporto = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  let normalized = raw.replace(/\s/g, "").replace(/€/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const isValidMonthKey = (value) =>
  /^\d{4}-\d{2}$/.test(String(value ?? "").trim());

const parseDataIntervento = (val) => {
  if (!val || String(val).trim() === "") return null;
  let s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s + "T00:00:00");
    if (!isNaN(d.getTime())) return d;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [g, m, a] = s.split("/");
    const d = new Date(`${a}-${m}-${g}T00:00:00`);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

const settoriLegacy = [
  { value: "Edile", label: "Settore Edile" },
  { value: "Cucine", label: "Settore Equipment Cucine" },
  { value: "Intesa", label: "Settore Banca Intesa" },
  { value: "Elettrico", label: "Settore Elettrico" },
  { value: "FTV", label: "Settore Fotovoltaico" },
  { value: "ROBY", label: "Personale Roby" },
  { value: "Amministrazione", label: "Amministrazione" },
  { value: "Cessati", label: "Ex Dipendenti" },
];

const TALOS_STATUS_MAP = {
  aperto: "Aperto",
  assegnato: "Assegnato",
  eseguito: "Eseguito",
  caricato: "Caricato",
  fatturato: "Fatturato",
};
const VALID_TALOS_STATUSES = Object.keys(TALOS_STATUS_MAP);

const CsvImport = () => {
  const [importedData, setImportedData] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importType, setImportType] = useState("progetti");
  const [notice, setNotice] = useState(null);

  const { addDocument: addProjectDocument } = useFirestore("projects");
  const { addDocument: addAnaliticaDocument } = useFirestore("analitica");
  const { addDocument: addRicaviDocument } = useFirestore("ricavi");
  const { addDocument: addTicketDocument } = useFirestore("tickets");
  const { documents: settoriDocs } = useCollection("settori");
  const { documents: existingTickets } = useCollection("tickets");
  const { user } = useAuthContext();

  const importTypes = [
    { value: "progetti", label: "Progetti" },
    { value: "analitica", label: "Dati Analitici" },
    { value: "ricavi", label: "Ricavi" },
    { value: "tickets-talos", label: "Tickets Talos" },
  ];

  const processData = (data) => {
    if (importType === "progetti") {
      return processDataToProjects(data);
    } else if (importType === "analitica") {
      return processDataToAnalitica(data);
    } else if (importType === "ricavi") {
      return processDataToRicavi(data);
    } else if (importType === "tickets-talos") {
      return processDataToTicketsTalos(data);
    }
  };

  const processDataToProjects = (data) => {
    setIsProcessing(true);
    const errors = [];
    const projects = [];

    const validTipiProject = ["cantiere", "commessa", "centro-di-costo"];
    const validCategorie = [
      "Edile",
      "Cucine",
      "Intesa",
      "Elettrico",
      "FTV",
      "ROBY",
      "Amministrazione",
    ];

    data.forEach((row, index) => {
      try {
        const rowErrors = [];

        if (!row.nome || row.nome.trim() === "") {
          rowErrors.push(`Riga ${index + 1}: Campo 'nome' obbligatorio`);
        }
        if (!row.categoria || row.categoria.trim() === "") {
          rowErrors.push(`Riga ${index + 1}: Campo 'categoria' obbligatorio`);
        }
        if (!row.codice || row.codice.toString().trim() === "") {
          rowErrors.push(`Riga ${index + 1}: Campo 'codice' obbligatorio`);
        }
        if (!row.tipoProject || row.tipoProject.trim() === "") {
          rowErrors.push(`Riga ${index + 1}: Campo 'tipoProject' obbligatorio`);
        }

        if (row.codice && isNaN(Number(row.codice))) {
          rowErrors.push(
            `Riga ${index + 1}: Campo 'codice' deve essere un numero`,
          );
        }

        if (
          row.tipoProject &&
          !validTipiProject.includes(row.tipoProject.trim())
        ) {
          rowErrors.push(
            `Riga ${index + 1}: 'tipoProject' deve essere uno tra: ${validTipiProject.join(", ")}`,
          );
        }

        if (row.categoria && !validCategorie.includes(row.categoria.trim())) {
          rowErrors.push(
            `Riga ${index + 1}: 'categoria' deve essere uno tra: ${validCategorie.join(", ")}`,
          );
        }

        if (
          row.tipoProject === "cantiere" &&
          row.dataFine &&
          new Date(row.dataFine) <= new Date()
        ) {
          rowErrors.push(
            `Riga ${index + 1}: Per i cantieri, 'dataFine' deve essere una data futura`,
          );
        }

        if (row.tipoProject === "centro-di-costo") {
          if (row.cliente && row.cliente.trim() !== "") {
            rowErrors.push(
              `Riga ${index + 1}: I centri di costo non devono avere un cliente`,
            );
          }
          if (row.indirizzo && row.indirizzo.trim() !== "") {
            rowErrors.push(
              `Riga ${index + 1}: I centri di costo non devono avere una localizzazione`,
            );
          }
        } else {
          if (!row.cliente || row.cliente.trim() === "") {
            rowErrors.push(
              `Riga ${index + 1}: Campo 'cliente' obbligatorio per ${row.tipoProject}`,
            );
          }
          if (!row.indirizzo || row.indirizzo.trim() === "") {
            rowErrors.push(
              `Riga ${index + 1}: Campo 'indirizzo' obbligatorio per ${row.tipoProject}`,
            );
          }
        }

        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
          return;
        }

        const project = {
          nome: row.nome.trim(),
          dettagli: row.descrizione || "",
          tipoProject: row.tipoProject.trim(),
          categoria: row.categoria.trim(),
          scadenza:
            row.tipoProject === "cantiere" && row.dataFine
              ? timestamp.fromDate(new Date(row.dataFine))
              : null,
          comments: [],
          autore: {
            displayName: user.displayName,
            photoURL: user.photoURL,
            id: user.uid,
          },
          completato: row.stato === "completato" || false,
          preventivo:
            row.tipoProject === "cantiere" && row.preventivo
              ? parseFloat(row.preventivo) || 0
              : 0,
          cliente:
            row.tipoProject === "centro-di-costo" ? "" : row.cliente || "",
          localizzazione:
            row.tipoProject === "centro-di-costo" ? "" : row.indirizzo || "",
          codice: row.codice.toString(),
          costiSostenuti: 0,
          avanzamento: 0,
          createdAt: timestamp.fromDate(new Date()),
        };

        projects.push(project);
      } catch (error) {
        errors.push(
          `Riga ${index + 1}: Errore durante l'elaborazione - ${error.message}`,
        );
      }
    });

    setValidationErrors(errors);
    setProcessedData(projects);
    setIsProcessing(false);
  };

  const processDataToAnalitica = (data) => {
    setIsProcessing(true);
    const errors = [];
    const analiticaItems = [];

    data.forEach((row, index) => {
      try {
        const rowErrors = [];

        if (!row.settore || row.settore.trim() === "") {
          rowErrors.push(`Riga ${index + 1}: Campo 'settore' obbligatorio`);
        }

        const numericFields = [
          "ricavi",
          "materiali",
          "dipendenti",
          "lavorazioni",
          "struttura",
          "personale",
          "rappresentanza",
          "automezzi",
          "mese",
          "anno",
        ];
        numericFields.forEach((field) => {
          if (row[field] && isNaN(Number(row[field]))) {
            rowErrors.push(
              `Riga ${index + 1}: Campo '${field}' deve essere un numero`,
            );
          }
        });

        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
          return;
        }

        const analiticaItem = {
          settore: row.settore.trim(),
          mese: parseFloat(row.mese) || 0,
          anno: parseFloat(row.anno) || 0,
          ricavi: parseFloat(row.ricavi) || 0,
          materiali: parseFloat(row.materiali) || 0,
          dipendenti: parseFloat(row.dipendenti) || 0,
          lavorazioni: parseFloat(row.lavorazioni) || 0,
          struttura: parseFloat(row.struttura) || 0,
          personale: parseFloat(row.personale) || 0,
          rappresentanza: parseFloat(row.rappresentanza) || 0,
          automezzi: parseFloat(row.automezzi) || 0,
          autore: {
            displayName: user.displayName,
            photoURL: user.photoURL,
            id: user.uid,
          },
          createdAt: timestamp.fromDate(new Date()),
        };

        analiticaItems.push(analiticaItem);
      } catch (error) {
        errors.push(
          `Riga ${index + 1}: Errore durante l'elaborazione - ${error.message}`,
        );
      }
    });

    setValidationErrors(errors);
    setProcessedData(analiticaItems);
    setIsProcessing(false);
  };

  const processDataToRicavi = (data) => {
    setIsProcessing(true);
    const errors = [];
    const ricavi = [];

    data.forEach((row, index) => {
      try {
        const rowErrors = [];

        const meseCompetenza = String(
          row.meseCompetenza ?? row.mese_competenza ?? "",
        ).trim();
        const cliente = String(row.cliente ?? "").trim();
        const settore = String(row.settore ?? "").trim();
        const descrizione = String(row.descrizione ?? "").trim();
        const unitaMisura =
          String(row.unitaMisura ?? row.unita_misura ?? "nr").trim() || "nr";
        const numeroDocumento = String(
          row.numero_documento ?? row.numeroDocumento ?? "",
        ).trim();
        const luogoIntervento = String(
          row.luogoIntervento ?? row.luogo_intervento ?? "",
        ).trim();
        const nrFattura = String(row.nrFattura ?? row.nr_fattura ?? "").trim();
        const importoNumber = parseImporto(row.importo);

        if (!cliente)
          rowErrors.push(`Riga ${index + 1}: Campo 'cliente' obbligatorio`);
        if (!meseCompetenza)
          rowErrors.push(
            `Riga ${index + 1}: Campo 'meseCompetenza' obbligatorio`,
          );
        if (meseCompetenza && !isValidMonthKey(meseCompetenza))
          rowErrors.push(
            `Riga ${index + 1}: 'meseCompetenza' deve essere nel formato YYYY-MM`,
          );
        if (!settore)
          rowErrors.push(`Riga ${index + 1}: Campo 'settore' obbligatorio`);
        if (!descrizione)
          rowErrors.push(`Riga ${index + 1}: Campo 'descrizione' obbligatorio`);
        if (!unitaMisura)
          rowErrors.push(`Riga ${index + 1}: Campo 'unitaMisura' obbligatorio`);
        if (!Number.isFinite(importoNumber))
          rowErrors.push(`Riga ${index + 1}: Campo 'importo' non valido`);

        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
          return;
        }

        ricavi.push({
          cliente,
          nrFattura,
          numero_documento: numeroDocumento,
          luogoIntervento,
          meseCompetenza,
          settore,
          descrizione,
          unitaMisura,
          importo: importoNumber,
          createdBy: user.uid,
          createdByName: user.displayName || user.email || "",
          createdAt: timestamp.fromDate(new Date()),
        });
      } catch (error) {
        errors.push(
          `Riga ${index + 1}: Errore durante l'elaborazione - ${error.message}`,
        );
      }
    });

    setValidationErrors(errors);
    setProcessedData(ricavi);
    setIsProcessing(false);
  };

  const processDataToTicketsTalos = (data) => {
    setIsProcessing(true);
    const errors = [];
    const items = [];

    if (data && data.length > 0) {
      const headerKeys = Object.keys(data[0]).map((k) =>
        k
          .replace(/^\uFEFF/, "")
          .toLowerCase()
          .trim(),
      );
      const aliasMap = {
        nrticket: "nrticket",
        nr_ticket: "nrticket",
        numeroticket: "nrticket",
        numero_ticket: "nrticket",
        descrizione: "descrizione",
        desc: "descrizione",
        luogo: "luogo",
        settorenome: "settorenome",
        settore: "settorenome",
        settore_nome: "settorenome",
        status: "status",
        stato: "status",
        ordine: "ordine",
        nrordine: "ordine",
        nr_ordine: "ordine",
        note: "note",
        noteintervento: "note",
        note_intervento: "note",
        dataintervento: "dataintervento",
        data_intervento: "dataintervento",
        dataticket: "dataticket",
        data_ticket: "dataticket",
        datarichiesta: "dataticket",
        data_richiesta: "dataticket",
        importo: "importo",
        importototale: "importo",
        importo_totale: "importo",
        totale: "importo",
        valore: "importo",
        prezzo: "importo",
      };
      const normalizedHeader = headerKeys.map((k) => aliasMap[k] || k);
      const requiredFields = [
        "nrticket",
        "descrizione",
        "luogo",
        "settorenome",
      ];
      const missingRequired = requiredFields.filter(
        (f) => !normalizedHeader.includes(f),
      );
      if (missingRequired.length > 0) {
        const missingPretty = missingRequired.map((f) => {
          if (f === "nrticket") return "nrTicket";
          if (f === "settorenome") return "settoreNome";
          return f;
        });
        errors.push(
          `Header del CSV non valido. Mancano le colonne obbligatorie: ${missingPretty.join(", ")}`,
        );
      }
    }

    data.forEach((row, index) => {
      try {
        const rowErrors = [];
        const rawKeys = Object.keys(row);
        const normalizedRow = {};
        rawKeys.forEach((k) => {
          // Fix: rimuovi BOM UTF-8 + lowercase + trim
          const keyLower = k
            .replace(/^\uFEFF/, "")
            .toLowerCase()
            .trim();
          let target = null;
          switch (keyLower) {
            case "nrticket":
            case "nr_ticket":
            case "numeroticket":
            case "numero_ticket":
              target = "nrTicket";
              break;
            case "descrizione":
            case "desc":
              target = "descrizione";
              break;
            case "luogo":
              target = "luogo";
              break;
            case "settorenome":
            case "settore":
            case "settore_nome":
              target = "settoreNome";
              break;
            case "status":
            case "stato":
              target = "status";
              break;
            case "ordine":
            case "nrordine":
            case "nr_ordine":
              target = "ordine";
              break;
            case "note":
            case "noteintervento":
            case "note_intervento":
              target = "note";
              break;
            case "dataintervento":
            case "data_intervento":
              target = "dataIntervento";
              break;
            case "dataticket":
            case "data_ticket":
            case "datarichiesta":
            case "data_richiesta":
              target = "dataTicket";
              break;
            default:
              target = k;
          }
          // Fix: fuzzy match intestazione importo (casi tipici Excel)
          if (!target || target === k || target == null) {
            if (
              keyLower.includes("importo") ||
              keyLower.includes("totale") ||
              keyLower === "valore" ||
              keyLower === "prezzo"
            ) {
              target = "importo";
            }
            if (
              keyLower.includes("dataticket") ||
              keyLower.includes("data_ticket") ||
              keyLower.includes("datarichiesta") ||
              keyLower.includes("data_richiesta") ||
              (keyLower.includes("data") && keyLower.includes("richiesta")) ||
              (keyLower.includes("data") && keyLower.includes("ticket"))
            ) {
              target = "dataTicket";
            }
          } else {
            if (
              target !== "importo" &&
              (keyLower.includes("importo") ||
                keyLower.includes("totale") ||
                keyLower === "valore" ||
                keyLower === "prezzo")
            ) {
              target = "importo";
            }
            if (
              target !== "dataIntervento" &&
              target !== "dataTicket" &&
              (keyLower.includes("dataticket") ||
                keyLower.includes("data_ticket") ||
                keyLower.includes("datarichiesta") ||
                keyLower.includes("data_richiesta") ||
                (keyLower.includes("data") && keyLower.includes("richiesta")) ||
                (keyLower.includes("data") && keyLower.includes("ticket")))
            ) {
              target = "dataTicket";
            }
          }
          normalizedRow[target] = row[k];
        });

        const nrTicketRaw = String(normalizedRow.nrTicket ?? "").trim();
        const descrizioneRaw = String(normalizedRow.descrizione ?? "").trim();
        const luogoRaw = String(normalizedRow.luogo ?? "").trim();
        const settoreNomeRaw = String(normalizedRow.settoreNome ?? "").trim();
        const statusRaw = String(normalizedRow.status ?? "").trim();
        const ordineRaw = String(normalizedRow.ordine ?? "").trim();
        const noteRaw = String(normalizedRow.note ?? "").trim();
        const dataInterventoRaw = normalizedRow.dataIntervento ?? "";
        const dataTicketRaw = normalizedRow.dataTicket ?? "";
        const importoRaw = normalizedRow.importo;

        if (!nrTicketRaw)
          rowErrors.push(`Riga ${index + 1}: nrTicket è obbligatorio`);
        if (!descrizioneRaw)
          rowErrors.push(`Riga ${index + 1}: descrizione è obbligatoria`);
        if (!luogoRaw)
          rowErrors.push(`Riga ${index + 1}: luogo è obbligatorio`);
        if (!settoreNomeRaw)
          rowErrors.push(`Riga ${index + 1}: settoreNome è obbligatorio`);

        let status = statusRaw ? statusRaw.toLowerCase() : "aperto";
        if (statusRaw && !VALID_TALOS_STATUSES.includes(status)) {
          rowErrors.push(
            `Riga ${index + 1}: Status non valido. Ammessi: aperto, assegnato, eseguito, caricato, fatturato`,
          );
        }
        if (!statusRaw) status = "aperto";

        const ordine = ordineRaw || "";
        const note = noteRaw || "";

        let dataInterventoDate = null;
        if (dataInterventoRaw && String(dataInterventoRaw).trim() !== "") {
          dataInterventoDate = parseDataIntervento(dataInterventoRaw);
          if (!dataInterventoDate) {
            rowErrors.push(
              `Riga ${index + 1}: dataIntervento non valida: usa yyyy-MM-dd o dd/MM/yyyy`,
            );
          }
        }

        let dataTicketDate = null;
        if (dataTicketRaw && String(dataTicketRaw).trim() !== "") {
          dataTicketDate = parseDataIntervento(dataTicketRaw);
          if (!dataTicketDate) {
            rowErrors.push(
              `Riga ${index + 1}: dataTicket non valida: usa yyyy-MM-dd o dd/MM/yyyy`,
            );
          }
        }

        let parsedImporto = null;
        const importoIsEmpty =
          importoRaw === undefined ||
          importoRaw === null ||
          (typeof importoRaw === "string" && importoRaw.trim() === "");
        if (!importoIsEmpty) {
          const n = parseImporto(importoRaw);
          if (Number.isNaN(n) || n === undefined || n === null) {
            rowErrors.push(
              `Riga ${index + 1}: Importo non valido: usa un formato come 1.250,00 € oppure 1250.50`,
            );
          } else {
            parsedImporto = n;
          }
        }

        let matchedSector = null;
        if (settoreNomeRaw) {
          const nomeCsv = settoreNomeRaw.trim();
          const nomeCsvLow = nomeCsv.toLowerCase();

          const legacyObj = settoriLegacy.find((s) => {
            const vLow = (s.value || "").trim().toLowerCase();
            const lLow = (s.label || "").trim().toLowerCase();
            return (
              vLow === nomeCsvLow ||
              lLow === nomeCsvLow ||
              (vLow && vLow.includes(nomeCsvLow)) ||
              (nomeCsvLow && nomeCsvLow.includes(vLow)) ||
              (lLow && lLow.includes(nomeCsvLow)) ||
              (nomeCsvLow && nomeCsvLow.includes(lLow))
            );
          });

          const searchVariants = new Set();
          if (legacyObj) {
            searchVariants.add((legacyObj.value || "").trim());
            searchVariants.add((legacyObj.label || "").trim());
          }
          searchVariants.add(nomeCsv);
          const searchVariantsArr = [...searchVariants];

          const matchesVariant = (settoreDoc, variant) => {
            if (!variant) return false;
            const vLow = variant.toLowerCase();

            const candidati = [
              settoreDoc.id,
              settoreDoc.nome,
              settoreDoc.settoreNome,
              settoreDoc.settoreId,
            ]
              .filter(Boolean)
              .map((val) => String(val).trim().toLowerCase());

            return candidati.some(
              (c) =>
                c === vLow ||
                (vLow && c.includes(vLow)) ||
                (c && vLow.includes(c)),
            );
          };

          matchedSector = (settoriDocs || []).find((s) =>
            searchVariantsArr.some((variant) => matchesVariant(s, variant)),
          );

          if (!matchedSector) {
            rowErrors.push(
              `Riga ${index + 1}: Settore '${nomeCsv}' non riconosciuto. Valori accettati (come codice o nome): Edile / Settore Edile, Cucine / Settore Equipment Cucine, Elettrico / Settore Elettrico, FTV / Settore Fotovoltaico, Intesa / Settore Banca Intesa, ROBY / Personale Roby, Amministrazione, Cessati / Ex Dipendenti. Verifica che il settore esista nella collection 'settori' del DB (vengono controllati sia 'id' che 'nome', case-insensitive).`,
            );
          }
        }

        if (nrTicketRaw) {
          const nrTicketNorm = nrTicketRaw.toLowerCase();
          const duplicate = (existingTickets || []).find(
            (t) =>
              t.nrTicket &&
              String(t.nrTicket).trim().toLowerCase() === nrTicketNorm,
          );
          if (duplicate) {
            rowErrors.push(
              `Riga ${index + 1}: nrTicket '${nrTicketRaw}' già presente nel database (duplicato)`,
            );
          }
        }

        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
          return;
        }

        const statusLabel = TALOS_STATUS_MAP[status] || status;

        items.push({
          cliente: "MD Spa",
          descrizione: descrizioneRaw,
          luogo: luogoRaw,
          nrTicket: nrTicketRaw,
          tipo: "talos",
          tipoLabel: "Talos",
          status,
          statusLabel,
          settore: { id: matchedSector.id, nome: matchedSector.nome },
          settoreId: matchedSector.id,
          settoreNome: matchedSector.nome,
          ordine,
          note,
          dataIntervento: dataInterventoDate
            ? timestamp.fromDate(dataInterventoDate)
            : null,
          dataTicket: dataTicketDate
            ? timestamp.fromDate(dataTicketDate)
            : null,
          importo: parsedImporto,
          createdBy: user?.uid || null,
          createdByName: user?.displayName || user?.email || "Import CSV",
          createdAt: timestamp.now(),
          updatedAt: timestamp.now(),
          vistiRT: [],
          attachments: [],
        });
      } catch (error) {
        errors.push(
          `Riga ${index + 1}: Errore durante l'elaborazione - ${error.message}`,
        );
      }
    });

    setValidationErrors(errors);
    setProcessedData(items);
    setIsProcessing(false);
  };

  const saveDataToDatabase = async () => {
    if (!processedData || processedData.length === 0) {
      setNotice({ type: "error", text: "Nessun dato da salvare" });
      return;
    }

    setIsSaving(true);
    setNotice(null);
    let successCount = 0;
    let errorCount = 0;

    try {
      let addDocument;
      let itemType;
      if (importType === "progetti") {
        addDocument = addProjectDocument;
        itemType = "progetti";
      } else if (importType === "analitica") {
        addDocument = addAnaliticaDocument;
        itemType = "dati analitici";
      } else if (importType === "ricavi") {
        addDocument = addRicaviDocument;
        itemType = "ricavi";
      } else if (importType === "tickets-talos") {
        addDocument = addTicketDocument;
        itemType = "Tickets Talos";
      } else {
        addDocument = addRicaviDocument;
        itemType = "dati";
      }

      for (const item of processedData) {
        try {
          await addDocument(item);
          successCount++;
        } catch (error) {
          console.error(
            `Errore salvando ${itemType}:`,
            item.nome || item.settore || item.nrTicket,
            error,
          );
          errorCount++;
        }
      }

      if (successCount > 0) {
        setNotice({
          type: "success",
          text: `${successCount} ${itemType} salvati con successo`,
        });
      }
      if (errorCount > 0) {
        setNotice({
          type: "error",
          text: `${errorCount} ${itemType} non sono stati salvati`,
        });
      }

      if (successCount > 0) {
        setImportedData(null);
        setProcessedData(null);
        setValidationErrors([]);
      }
    } catch (error) {
      setNotice({ type: "error", text: "Errore durante il salvataggio" });
    }

    setIsSaving(false);
  };

  const handleDataImported = (data) => {
    setImportedData(data);
    setProcessedData(null);
    setValidationErrors([]);
  };

  return (
    <div className="csv-import-container">
      {notice ? (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
            background: notice.type === "success" ? "#e8f5e9" : "#ffebee",
            color: notice.type === "success" ? "#1b5e20" : "#b71c1c",
            border:
              notice.type === "success"
                ? "1px solid #c8e6c9"
                : "1px solid #ffcdd2",
          }}
        >
          {notice.text}
        </div>
      ) : null}

      <div className="csv-import-header">
        <h1>Importazione Dati da CSV</h1>

        <div className="import-type-selection">
          <label htmlFor="import-type">
            <strong>Tipo di importazione:</strong>
          </label>
          <select
            id="import-type"
            value={importType}
            onChange={(e) => {
              setImportType(e.target.value);
              setImportedData(null);
              setProcessedData(null);
              setValidationErrors([]);
            }}
            className="import-type-select"
          >
            {importTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p>
            <strong>
              Campi supportati per{" "}
              {importTypes.find((t) => t.value === importType)?.label}:
            </strong>
          </p>
          {importType === "progetti" ? (
            <ul>
              <li>
                <strong>Obbligatori:</strong> nome, categoria, codice
                (numerico), tipoProject
              </li>
              <li>
                <strong>tipoProject validi:</strong> cantiere, commessa,
                centro-di-costo
              </li>
              <li>
                <strong>categoria valide:</strong> Edile, Cucine, Intesa,
                Elettrico, FTV, ROBY, Amministrazione
              </li>
              <li>
                <strong>Opzionali:</strong> descrizione, preventivo, dataFine
                (per cantieri), cliente, indirizzo, stato, note
              </li>
              <li>
                <strong>Note:</strong> Per centro-di-costo, cliente e indirizzo
                vengono ignorati
              </li>
            </ul>
          ) : importType === "analitica" ? (
            <ul>
              <li>
                <strong>Obbligatori:</strong> settore
              </li>
              <li>
                <strong>Numerici (opzionali):</strong> mese, anno, ricavi,
                materiali, dipendenti, lavorazioni, struttura, personale,
                rappresentanza, automezzi
              </li>
              <li>
                <strong>Note:</strong> I campi numerici vengono impostati a 0 se
                non specificati o non validi
              </li>
            </ul>
          ) : importType === "tickets-talos" ? (
            <div className="csv-help-block">
              <h4>📄 Formato file Tickets Talos</h4>
              <p>
                <strong>Campi obbligatori</strong>:
              </p>
              <ul>
                <li>
                  <code>nrTicket</code> — numero ticket (univoco, es.
                  TAL-2026-001). Alias: nr_ticket, numeroTicket, numero_ticket.
                </li>
                <li>
                  <code>descrizione</code> — descrizione intervento. Alias:
                  desc.
                </li>
                <li>
                  <code>luogo</code> — luogo dell'intervento (es. Sede Milano).
                </li>
                <li>
                  <code>settoreNome</code> — nome settore (es. Settore Edile,
                  Personale Roby, Edile...). Alias: settore, settore_nome.
                  Supporta sia nome umano che codice legacy (valori dell'app).
                </li>
              </ul>
              <p>
                <strong>Campi opzionali</strong>:
              </p>
              <ul>
                <li>
                  <code>status</code> — valore tra: aperto, assegnato, eseguito,
                  caricato, fatturato. Default se omesso: <code>aperto</code>.
                  Alias: stato.
                </li>
                <li>
                  <code>ordine</code> — numero ordine collegato. Alias:
                  nrOrdine, nr_ordine.
                </li>
                <li>
                  <code>note</code> — testo libero dettagli. Alias:
                  noteIntervento, note_intervento.
                </li>
                <li>
                  <code>dataIntervento</code> — data intervento: accettati{" "}
                  <code>dd/MM/yyyy</code> (italiano) o <code>yyyy-MM-dd</code>{" "}
                  (ISO). Alias: data_intervento.
                </li>
                <li>
                  <code>importo</code> — importo in euro (es. 1.250,00 € o
                  1250.50). Alias supportati: importoTotale, importo_totale,
                  totale.
                </li>
              </ul>
              <p className="csv-help-note">
                Nota: Cliente = MD Spa e Tipo = Talos sono valorizzati
                automaticamente. Il numero ticket viene controllato per
                univocità prima dell'import.
              </p>
            </div>
          ) : (
            <ul>
              <li>
                <strong>Obbligatori:</strong> meseCompetenza (YYYY-MM), cliente,
                settore, descrizione, unitaMisura, importo
              </li>
              <li>
                <strong>Opzionali:</strong> nrFattura, numero_documento,
                luogoIntervento
              </li>
              <li>
                <strong>Note:</strong> unitaMisura default 'nr' se non presente
              </li>
            </ul>
          )}
        </div>
      </div>

      <div className="import-section">
        <ExcelImporter onDataImported={handleDataImported} />
      </div>

      {importedData && (
        <div className="processing-section">
          <h3>Dati Importati ({importedData.length} righe)</h3>
          <button
            className="btn-process"
            onClick={() => processData(importedData)}
            disabled={isProcessing}
          >
            {isProcessing ? "Elaborazione in corso..." : "Elabora Dati"}
          </button>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <h3>Errori di Validazione</h3>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index} className="error-item">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {processedData && processedData.length > 0 && (
        <div className="preview-section">
          <h3>
            Anteprima{" "}
            {importType === "progetti"
              ? "Progetti"
              : importType === "analitica"
                ? "Dati Analitici"
                : importType === "ricavi"
                  ? "Ricavi"
                  : importType === "tickets-talos"
                    ? "Tickets Talos"
                    : "Dati"}{" "}
            ({processedData.length} elementi validi)
          </h3>
          <div className="projects-preview">
            {processedData.slice(0, 5).map((item, index) => (
              <div key={index} className="project-preview-card">
                {importType === "progetti" ? (
                  <>
                    <h4>{item.nome}</h4>
                    <p>
                      <strong>Codice:</strong> {item.codice}
                    </p>
                    <p>
                      <strong>Categoria:</strong> {item.categoria}
                    </p>
                    <p>
                      <strong>Tipo Project:</strong> {item.tipoProject}
                    </p>
                    {item.dettagli && (
                      <p>
                        <strong>Descrizione:</strong> {item.dettagli}
                      </p>
                    )}
                    {item.preventivo > 0 && (
                      <p>
                        <strong>Preventivo:</strong> €
                        {item.preventivo.toLocaleString()}
                      </p>
                    )}
                    {item.cliente && (
                      <p>
                        <strong>Cliente:</strong> {item.cliente}
                      </p>
                    )}
                    <p>
                      <strong>Completato:</strong>{" "}
                      {item.completato ? "Sì" : "No"}
                    </p>
                  </>
                ) : importType === "analitica" ? (
                  <>
                    <h4>{item.settore}</h4>
                    <p>
                      <strong>Ricavi:</strong> €{item.ricavi.toLocaleString()}
                    </p>
                    <p>
                      <strong>Materiali:</strong> €
                      {item.materiali.toLocaleString()}
                    </p>
                    <p>
                      <strong>Dipendenti:</strong> {item.dipendenti}
                    </p>
                    <p>
                      <strong>Lavorazioni:</strong> €
                      {item.lavorazioni.toLocaleString()}
                    </p>
                    <p>
                      <strong>Struttura:</strong> €
                      {item.struttura.toLocaleString()}
                    </p>
                    <p>
                      <strong>Personale:</strong> €
                      {item.personale.toLocaleString()}
                    </p>
                    <p>
                      <strong>Rappresentanza:</strong> €
                      {item.rappresentanza.toLocaleString()}
                    </p>
                    <p>
                      <strong>Automezzi:</strong> €
                      {item.automezzi.toLocaleString()}
                    </p>
                  </>
                ) : importType === "tickets-talos" ? (
                  <>
                    <h4
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        Nr. {item.nrTicket}
                      </span>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background:
                            item.status === "aperto"
                              ? "#fff3e0"
                              : item.status === "assegnato"
                                ? "#e3f2fd"
                                : item.status === "eseguito"
                                  ? "#e8f5e9"
                                  : item.status === "caricato"
                                    ? "#f3e5f5"
                                    : "#e0f2f1",
                          color:
                            item.status === "aperto"
                              ? "#e65100"
                              : item.status === "assegnato"
                                ? "#0d47a1"
                                : item.status === "eseguito"
                                  ? "#1b5e20"
                                  : item.status === "caricato"
                                    ? "#4a148c"
                                    : "#004d40",
                        }}
                      >
                        {item.statusLabel}
                      </span>
                    </h4>
                    <p>
                      <strong>Descrizione:</strong> {item.descrizione}
                    </p>
                    <p>
                      <strong>Luogo:</strong> {item.luogo}
                    </p>
                    <p>
                      <strong>Settore:</strong> {item.settoreNome}
                    </p>
                    <p>
                      <strong>Ordine:</strong> {item.ordine || "—"}
                    </p>
                    <p>
                      <strong>Data ticket (richiesta):</strong>{" "}
                      {item.dataTicket && item.dataTicket.toDate
                        ? format(item.dataTicket.toDate(), "dd/MM/yyyy")
                        : "—"}
                    </p>
                    <p>
                      <strong>Data intervento:</strong>{" "}
                      {item.dataIntervento && item.dataIntervento.toDate
                        ? format(item.dataIntervento.toDate(), "dd/MM/yyyy")
                        : "—"}
                    </p>
                    <p>
                      <strong>Importo:</strong>{" "}
                      {item.importo !== null && item.importo !== undefined
                        ? `€${Number(item.importo).toLocaleString("it-IT")}`
                        : "—"}
                    </p>
                    <p>
                      <strong>Cliente:</strong> {item.cliente} ·{" "}
                      <strong>Tipo:</strong> {item.tipoLabel}
                    </p>
                    {item.note && (
                      <p>
                        <strong>Note:</strong> {item.note}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h4>{item.descrizione}</h4>
                    <p>
                      <strong>Mese competenza:</strong> {item.meseCompetenza}
                    </p>
                    <p>
                      <strong>Cliente:</strong> {item.cliente}
                    </p>
                    <p>
                      <strong>Settore:</strong> {item.settore}
                    </p>
                    <p>
                      <strong>UM:</strong> {item.unitaMisura}
                    </p>
                    <p>
                      <strong>Importo:</strong> €
                      {Number(item.importo || 0).toLocaleString()}
                    </p>
                    {item.nrFattura ? (
                      <p>
                        <strong>Nr fattura:</strong> {item.nrFattura}
                      </p>
                    ) : null}
                    {item.numero_documento ? (
                      <p>
                        <strong>Riferimento:</strong> {item.numero_documento}
                      </p>
                    ) : null}
                    {item.luogoIntervento ? (
                      <p>
                        <strong>Luogo:</strong> {item.luogoIntervento}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ))}
            {processedData.length > 5 && (
              <p className="more-projects">
                ... e altri {processedData.length - 5} elementi
              </p>
            )}
          </div>

          <div className="save-section">
            <button
              className="btn-save"
              onClick={saveDataToDatabase}
              disabled={isSaving}
            >
              {isSaving
                ? "Salvataggio in corso..."
                : `Salva ${processedData.length} ${
                    importType === "progetti"
                      ? "Progetti"
                      : importType === "analitica"
                        ? "Dati Analitici"
                        : importType === "ricavi"
                          ? "Ricavi"
                          : importType === "tickets-talos"
                            ? "Tickets Talos"
                            : "Dati"
                  } nel Database`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvImport;
